import React, { useCallback, useEffect, useState } from "react";
import { FormControl, InputLabel, MenuItem, Select, Stack } from "@mui/material";
import type { AnswerSet, AnswerValue } from "../../../../../../types/answerSet";
import type { Question } from "../../../../../../types/rubric";
import type { OperandDto } from "../../../../../../types/resolver";
import { fetchAnswerSet, fetchQuestion, fetchQuestionsPaginated } from "../../../../../../utils/api";
import { OPERAND_TYPES, OPERAND_TYPES_LEFT, toNum } from "./constants";
import type { OperandBuilderProps } from "./types";
import { LiteralOperand } from "./LiteralOperand";
import { RightLiteralOnly } from "./RightLiteralOnly";
import { RightAnswerSameQuestion } from "./RightAnswerSameQuestion";
import { AnswerOperand } from "./AnswerOperand";
import { DerivedAttributeOperand } from "./DerivedAttributeOperand";
import { KeyOperand } from "./KeyOperand";
import { WorkItemAttributeOperand } from "./WorkItemAttributeOperand";

const LEFT_TYPES_KEY_ONLY = [{ value: "key", label: "Key (context path)" }] as const;
const LEFT_TYPES_KEY_AND_DERIVED = [
    { value: "key", label: "Key (context path)" },
    { value: "derivedAttribute", label: "Derived attribute" },
] as const;

export const OperandBuilder: React.FC<OperandBuilderProps> = ({
    value,
    onChange,
    label = "Operand",
    side,
    leftOperand,
    evaluationDomain,
    declaredDerivedAttributeNames = [],
    leftOperandTypes = "all",
    rightNumericOnly = false,
}) => {
    const leftKeyOnly = side === "left" && leftOperandTypes === "keyOnly";
    const leftKeyAndDerivedOnly = side === "left" && leftOperandTypes === "keyAndDerivedOnly";
    const typesForSide = leftKeyOnly
        ? LEFT_TYPES_KEY_ONLY
        : leftKeyAndDerivedOnly
            ? LEFT_TYPES_KEY_AND_DERIVED
            : side === "left"
                ? OPERAND_TYPES_LEFT
                : OPERAND_TYPES;
    const defaultType = side === "left" ? "key" : "literal";
    const hasOperandType = value?.operandType != null;
    const rawType = hasOperandType ? value.operandType : defaultType;
    const effectiveType: string = leftKeyOnly
        ? "key"
        : leftKeyAndDerivedOnly && rawType !== "key" && rawType !== "derivedAttribute"
            ? "key"
            : side === "left" && rawType !== "answer" && rawType !== "derivedAttribute" && rawType !== "key"
                ? defaultType
                : rawType;

    const [questionSearch, setQuestionSearch] = useState("");
    const [questionOptions, setQuestionOptions] = useState<Question[]>([]);
    const [questionLoading, setQuestionLoading] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<Question | null>(null);
    const [rightAnswerSet, setRightAnswerSet] = useState<AnswerSet | null>(null);
    const [rightAnswerSetLoading, setRightAnswerSetLoading] = useState(false);

    const qIdNum = effectiveType === "answer" ? toNum(value?.questionId) : undefined;
    const rightSideAnswerOfSameQuestion = side === "right" && leftOperand?.operandType === "answer";
    const rightSideLiteralOnly = side === "right" && (leftOperand?.operandType === "derivedAttribute" || leftOperand?.operandType === "key");

    useEffect(() => {
        if (effectiveType !== "answer" || qIdNum == null) {
            setSelectedQuestion(null);
            return;
        }
        let cancelled = false;
        if (!selectedQuestion || selectedQuestion.id !== qIdNum) {
            setQuestionLoading(true);
            fetchQuestion(qIdNum)
                .then((q) => {
                    if (!cancelled) setSelectedQuestion(q);
                })
                .catch(() => {
                    if (!cancelled) setSelectedQuestion(null);
                })
                .finally(() => {
                    if (!cancelled) setQuestionLoading(false);
                });
        }
        return () => {
            cancelled = true;
        };
    }, [effectiveType, qIdNum]); // eslint-disable-line react-hooks/exhaustive-deps -- only when questionId from value changes

    useEffect(() => {
        if (!rightSideAnswerOfSameQuestion || !leftOperand) {
            setRightAnswerSet(null);
            return;
        }
        const asId = toNum(leftOperand.answerSetId);
        const qId = toNum(leftOperand.questionId);
        if (asId == null && qId == null) {
            setRightAnswerSet(null);
            return;
        }
        let cancelled = false;
        setRightAnswerSetLoading(true);
        const load = asId != null
            ? fetchAnswerSet(asId)
            : fetchQuestion(qId!).then((q) => {
                if (q.answers?.length) return { id: 0, name: q.answerSetName ?? "", answers: q.answers } as AnswerSet;
                const id = q.answerSetId != null ? Number(q.answerSetId) : null;
                return id != null ? fetchAnswerSet(id) : Promise.resolve(null);
            });
        load
            .then((as) => {
                if (!cancelled && as) setRightAnswerSet(as);
                else if (!cancelled) setRightAnswerSet(null);
            })
            .catch(() => {
                if (!cancelled) setRightAnswerSet(null);
            })
            .finally(() => {
                if (!cancelled) setRightAnswerSetLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [rightSideAnswerOfSameQuestion, leftOperand?.answerSetId, leftOperand?.questionId]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchQuestions = useCallback(async (search: string) => {
        if (!search.trim()) {
            setQuestionOptions([]);
            return;
        }
        setQuestionLoading(true);
        try {
            const page = await fetchQuestionsPaginated({
                search: search.trim(),
                size: 20,
                sortBy: "name",
                sortDirection: "asc",
            });
            setQuestionOptions(page.content ?? []);
        } catch {
            setQuestionOptions([]);
        } finally {
            setQuestionLoading(false);
        }
    }, []);

    useEffect(() => {
        if (effectiveType !== "answer") return;
        const t = setTimeout(() => fetchQuestions(questionSearch), 300);
        return () => clearTimeout(t);
    }, [effectiveType, questionSearch, fetchQuestions]);

    const handleQuestionChange = (_: unknown, q: Question | null) => {
        setSelectedQuestion(q ?? null);
        if (!q) {
            onChange({ operandType: "answer", answerSetId: "" });
            return;
        }
        const asId = q.answerSetId != null ? String(q.answerSetId) : "";
        onChange({
            operandType: "answer",
            questionId: q.id,
            answerSetId: asId,
        });
    };

    const getQuestionLabel = (q: Question) => q.name || q.questionText || `Question ${q.id}`;

    const clearAnswerFields = (payload: OperandDto): OperandDto => ({
        ...payload,
        questionId: undefined,
        answerSetId: undefined,
        answerId: undefined,
    });

    const handleTypeChange = (operandType: string) => {
        if (operandType === "literal") {
            onChange(clearAnswerFields({ value: "" }));
            return;
        }
        if (operandType === "answer") {
            onChange({ operandType: "answer", answerSetId: "" });
            return;
        }
        if (operandType === "derivedAttribute") {
            onChange(clearAnswerFields({ operandType: "derivedAttribute", derivedAttributeName: "" }));
            return;
        }
        if (operandType === "key") {
            onChange(clearAnswerFields({ operandType: "key", key: "" }));
            return;
        }
        if (operandType === "workItemAttribute") {
            onChange(clearAnswerFields({ operandType: "workItemAttribute", workItemAttributeName: "" }));
            return;
        }
        onChange(clearAnswerFields({ value: "" }));
    };

    useEffect(() => {
        if (leftKeyOnly && value?.operandType != null && value.operandType !== "key") {
            onChange(clearAnswerFields({ operandType: "key", key: value?.key ?? "" }));
        }
    }, [leftKeyOnly]); // eslint-disable-line react-hooks/exhaustive-deps -- normalize to key when rule restricts to key only

    useEffect(() => {
        if (
            leftKeyAndDerivedOnly &&
            value?.operandType != null &&
            value.operandType !== "key" &&
            value.operandType !== "derivedAttribute"
        ) {
            onChange(clearAnswerFields({ operandType: "key", key: value?.key ?? "" }));
        }
    }, [leftKeyAndDerivedOnly]); // eslint-disable-line react-hooks/exhaustive-deps -- normalize when rule restricts to key/derived only

    if (rightSideAnswerOfSameQuestion) {
        return (
            <RightAnswerSameQuestion
                value={value}
                onChange={onChange}
                answerOptions={rightAnswerSet?.answers ?? []}
                loading={rightAnswerSetLoading}
            />
        );
    }

    if (rightSideLiteralOnly) {
        return <RightLiteralOnly value={value} onChange={onChange} numericOnly={rightNumericOnly} />;
    }

    if (side === "right" && rightNumericOnly) {
        return <RightLiteralOnly value={value} onChange={onChange} numericOnly />;
    }

    return (
        <Stack spacing={1}>
            <FormControl size="small" fullWidth>
                <InputLabel id="operand-type-label">{label} type</InputLabel>
                <Select
                    labelId="operand-type-label"
                    label={`${label} type`}
                    value={effectiveType}
                    onChange={(e) => handleTypeChange(e.target.value)}
                >
                    {typesForSide.map((t) => (
                        <MenuItem key={t.value} value={t.value}>
                            {t.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            {effectiveType === "literal" && <LiteralOperand value={value} onChange={onChange} />}
            {effectiveType === "answer" && (
                <AnswerOperand
                    value={value}
                    onChange={onChange}
                    questionSearch={questionSearch}
                    onQuestionSearchChange={setQuestionSearch}
                    questionOptions={questionOptions}
                    questionLoading={questionLoading}
                    selectedQuestion={selectedQuestion}
                    onQuestionChange={(q) => handleQuestionChange(null, q)}
                    getQuestionLabel={getQuestionLabel}
                />
            )}
            {effectiveType === "derivedAttribute" && (
                <DerivedAttributeOperand
                    value={value}
                    onChange={onChange}
                    declaredDerivedAttributeNames={declaredDerivedAttributeNames}
                />
            )}
            {effectiveType === "key" && (
                <KeyOperand value={value} onChange={onChange} evaluationDomain={evaluationDomain} />
            )}
            {effectiveType === "workItemAttribute" && <WorkItemAttributeOperand value={value} onChange={onChange} />}
        </Stack>
    );
};
